"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GroupNavigator = void 0;
/**
 * Utility class for navigating recursive Groupings of data or components, where Groupings are tree structures
 * whose children are either 'leaf' data items (T) or further Groupings of T.
 */
var GroupNavigator = /** @class */ (function () {
    function GroupNavigator(children, navigatorProps) {
        var _this = this;
        if (navigatorProps === void 0) { navigatorProps = {}; }
        this.children = children;
        this.navigatorProps = navigatorProps;
        this.currentIndex = null;
        this.child = null;
        /**
         * Get the next leaf item, walking down the tree recursively until one is found. Navigable children (groups)
         * are entered immediately, falsy items are skipped.
         * @returns next leaf data item (T) or null if one cannot be found.
         */
        this.nextItem = function () {
            if (_this.children.every(function (c) { return !c; })) {
                return null;
            }
            return ((childIsNavigable(_this.child) && _this.child.navigator.nextItem()) ||
                (_this.nextChild() &&
                    ((!childIsNavigable(_this.child) && _this.child) || _this.nextItem())) ||
                null);
        };
        /**
         * Get the previous leaf item, walking down the tree recursively until one is found. Navigable children (groups)
         * are entered immediately, falsy items are skipped.
         * @returns previous leaf data item (T) or null if one cannot be found.
         */
        this.previousItem = function () {
            if (_this.children.every(function (c) { return !c; })) {
                return null;
            }
            return ((childIsNavigable(_this.child) && _this.child.navigator.previousItem()) ||
                (_this.previousChild() &&
                    ((!childIsNavigable(_this.child) && _this.child) ||
                        _this.previousItem())) ||
                null);
        };
        /**
         * Leave the current level of the group hierarchy after recursively leaving any children. Fires onLeave event if provided.
         */
        this.exit = function () {
            var current = _this.child;
            if (childIsNavigable(current)) {
                current.navigator.exit();
            }
            if (_this.currentIndex !== null && _this.navigatorProps.onLeave) {
                _this.navigatorProps.onLeave();
            }
            _this.currentIndex = null;
            _this.setChild();
        };
        /**
         * Move to the next direct child, wrapping to the beginning/end of the children array if wrapping is enabled.
         * Fires the onActiveChildChanged event if one is provided.
         * @returns next item (T) or INavigableGroup<T>
         */
        this.nextChild = function () {
            var newIndex = _this.currentIndex === null ? 0 : _this.currentIndex + 1;
            return _this.resolveIndexToChild(newIndex);
        };
        /**
         * Move to the previous direct child, wrapping to the beginning/end of the children array if wrapping is enabled.
         * Fires the onActiveChildChanged event if one is provided.
         * @returns previous item (T) or INavigableGroup<T>
         */
        this.previousChild = function () {
            var newIndex = _this.currentIndex === null
                ? _this.children.length - 1
                : _this.currentIndex - 1;
            return _this.resolveIndexToChild(newIndex);
        };
        // PRIVATE METHODS -----------------------------------------------------------
        this.resolveIndexToChild = function (index) {
            var result = null;
            if (_this.indexInRange(index)) {
                result = index;
            }
            else if (_this.navigatorProps.wrap) {
                result = index < 0 ? _this.children.length - 1 : 0;
            }
            else {
                _this.exit();
                result = null;
            }
            if (_this.currentIndex === null &&
                result !== null &&
                _this.navigatorProps.onEnter) {
                _this.navigatorProps.onEnter();
            }
            _this.currentIndex = result;
            return _this.setChild();
        };
        this.indexInRange = function (index) {
            return index !== null && index >= 0 && index < _this.children.length;
        };
        this.setChild = function () {
            var newChild = _this.indexInRange(_this.currentIndex)
                ? _this.children[_this.currentIndex]
                : null;
            if (newChild !== _this.child && _this.navigatorProps.onActiveChildChanged) {
                _this.navigatorProps.onActiveChildChanged(newChild);
            }
            return (_this.child = newChild);
        };
    }
    return GroupNavigator;
}());
exports.GroupNavigator = GroupNavigator;
var childIsNavigable = function (child) {
    return (Boolean(child) &&
        child.navigator instanceof GroupNavigator);
};
